import { Product } from "@domain/product/product";
import { ProductConfiguration } from "@domain/project/configurations/product-configuration";
import type { FgdsProject } from "@domain/project/fgds-project";
import { ProjectEventType } from "@domain/project/project-event";
import type { ScopedProject } from "@domain/project/project-guards";
import { DEFAULT_TRANSMITTER_DETECTION_RANGE } from "@lib/constants/transmitter-defaults";
import { ProductItem } from "@project/product-item";
import { TransmitterType } from "@project/transmitter-configuration/transmitter-family-selector/transmitter-type";
import { Expose, Transform, Type } from "class-transformer";
import { IsArray, IsNumber, IsOptional, IsString, ValidateNested } from "class-validator";
import { v4 as uuidv4 } from "uuid";

export class TransmitterConfiguration extends ProductConfiguration {
  private static readonly NAME_REGEX = /((?:\bPolytron\b|\bPIR\b)\s\d+)/g;
  static readonly DEFAULT_DETECTION_RANGE = DEFAULT_TRANSMITTER_DETECTION_RANGE;

  @IsString()
  public productId: string;

  @Expose()
  @IsArray()
  @ValidateNested({ each: true })
  @Type(() => ProductItem)
  @Transform(({ value, obj }) => {
    // migrate attachmentIds for backward compatibility
    if (Array.isArray(value)) return value;
    if (Array.isArray(obj.attachmentIds)) {
      return obj.attachmentIds.map((id: string) => ProductItem.create(id));
    }
    return [];
  })
  attachments: ProductItem[];

  @IsOptional()
  @IsString()
  public gasId?: string;

  @IsOptional()
  @IsString()
  public sensorId?: string;

  @IsOptional()
  @IsString()
  public family?: string;

  @IsNumber()
  @Transform(
    (params) =>
      params.value === undefined || params.value === null ? TransmitterConfiguration.DEFAULT_DETECTION_RANGE : params.value,
    { toClassOnly: true },
  )
  public defaultDetectionRange: number = TransmitterConfiguration.DEFAULT_DETECTION_RANGE;

  constructor(
    id: string,
    name: string,
    project: ScopedProject,
    productId: string,
    attachments: ProductItem[],
    gasId?: string,
    sensorId?: string,
    defaultDetectionRange: number = TransmitterConfiguration.DEFAULT_DETECTION_RANGE,
    family?: string,
  ) {
    super(id, name, project);
    this.productId = productId;
    this.attachments = attachments;
    this.gasId = gasId;
    this.sensorId = sensorId;
    this.defaultDetectionRange = defaultDetectionRange;
    this.family = family;
  }

  static create(
    name: string,
    project: ScopedProject,
    productId: string,
    attachments: ProductItem[],
    gasId?: string,
    sensorId?: string,
    defaultDetectionRange: number = TransmitterConfiguration.DEFAULT_DETECTION_RANGE,
    family?: string,
  ): TransmitterConfiguration {
    return new TransmitterConfiguration(
      uuidv4(),
      name,
      project,
      productId,
      attachments,
      gasId,
      sensorId,
      defaultDetectionRange,
      family,
    );
  }

  override get productDataName(): string {
    return TransmitterConfiguration.getProductDataName(this.name);
  }

  countPlacedProducts(project: ScopedProject = this.project): number {
    return project ? project.countPlacedTransmitters(this) : 0;
  }

  update(gasId: string, transmitter: Product, attachments: ProductItem[], sensorId?: string, family?: TransmitterType) {
    this.gasId = gasId;
    this.name = transmitter.name;
    this.productId = transmitter.id;
    this.sensorId = sensorId;
    this.family = family?.key;
    this.attachments = attachments;
    this.project.publishUpdate(ProjectEventType.TRANSMITTER_CONFIGURATION_UPDATED, this);
  }

  updateDefaultDetectionRange(value: number) {
    if (!Number.isFinite(value) || value <= 0) {
      return;
    }
    if (this.defaultDetectionRange === value) {
      return;
    }
    this.defaultDetectionRange = value;
    (this.project as FgdsProject).applyTransmitterConfigDefaultDetectionRange(this);
  }

  override get mainProductId(): string {
    return this.productId;
  }

  override get configuredProductIds(): string[] {
    const productIds = this.attachmentIds.concat(this.productId);
    if (this.sensorId) {
      productIds.push(this.sensorId);
    }
    return productIds;
  }

  get attachmentIds(): string[] {
    return this.attachments.map((attachment) => attachment.productId);
  }

  get defaultMonitoringAreaRadius(): number {
    return this.defaultDetectionRange;
  }

  static getProductDataName(name: string) {
    const regexMatch = name.match(TransmitterConfiguration.NAME_REGEX);
    if (!regexMatch || !regexMatch.length) {
      return "";
    }
    return regexMatch[0].replace(" ", "_");
  }
}
